import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarDays, ChartColumn, ChevronDown, Database, IdCard, MoreVertical } from "lucide-react";
import { SAMPLE_CLIENTS, SAMPLE_METRICS } from "../sample-data";

// Reseller home (Figma "Partner Console"). No reseller API yet — the
// numbers and client list come from ../sample-data until one exists, and the
// date range only changes the label for now.

const RANGE_PRESETS = [
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
];

const fmtDay = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
const rangeLabel = (days: number) => {
  const end = new Date();
  const start = new Date(end.getTime() - days * 86_400_000);
  return `${fmtDay(start)} - ${fmtDay(end)} ${end.getFullYear()}`;
};

const compact = (n: number) => new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
const money = (n: number, currency: string) => new Intl.NumberFormat("en", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);

interface StatCardProps {
  icon: React.ElementType;
  title: string;
  value: string;
  description: string;
  linkLabel: string;
  onLink?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, title, value, description, linkLabel, onLink }) => (
  <div className="flex flex-col rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
    <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
      <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
      {title}
    </p>
    <p className="mt-3 text-xl font-medium text-gray-900 dark:text-gray-100">{value}</p>
    <p className="mt-3 text-xs text-gray-600 dark:text-gray-400 leading-relaxed flex-1">{description}</p>
    <button
      type="button"
      onClick={onLink}
      className="mt-3 inline-flex items-center gap-1.5 self-start text-sm font-medium text-indigo-700 dark:text-indigo-400 underline underline-offset-2 hover:text-indigo-800"
    >
      {linkLabel}
      <ArrowRight className="w-4 h-4" />
    </button>
  </div>
);

const PartnerConsole: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState(RANGE_PRESETS[0]);
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!rangeOpen) return;
    const close = (e: MouseEvent) => {
      if (rangeRef.current && !rangeRef.current.contains(e.target as Node)) setRangeOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [rangeOpen]);

  const scrollToClients = () => tableRef.current?.scrollIntoView({ behavior: "smooth" });
  const m = SAMPLE_METRICS;

  const cards: StatCardProps[] = [
    {
      icon: Database,
      title: "Total Clients",
      value: String(SAMPLE_CLIENTS.length),
      description: "Active customer workspaces currently managed through your reseller account.",
      linkLabel: "View Clients",
      onLink: scrollToClients,
    },
    {
      icon: IdCard,
      title: "Total Profiles",
      value: compact(m.totalProfiles),
      description: "Customer profiles available across all active client workspaces.",
      linkLabel: "View Profiles",
    },
    {
      icon: ChartColumn,
      title: "Revenue",
      value: money(m.revenue, m.revenueCurrency),
      description: "Revenue generated from active client plans and recurring subscriptions.",
      linkLabel: "View",
    },
    {
      icon: Database,
      title: "Events Processed",
      value: compact(m.eventsProcessed),
      description: "Customer, campaign, conversation, and behavioural events processed across clients.",
      linkLabel: "View Clients",
      onLink: scrollToClients,
    },
    {
      icon: IdCard,
      title: "Connected Sources",
      value: String(m.connectedSources),
      description: "Data sources currently connected across all client workspaces.",
      linkLabel: "View Profiles",
    },
    {
      icon: ChartColumn,
      title: "Active Destinations",
      value: String(m.activeDestinations),
      description: "Destinations currently receiving synced audiences, profiles, or customer data.",
      linkLabel: "View",
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 min-h-full">
      <h1 className="text-2xl font-medium text-gray-900 dark:text-gray-100">Partner Console</h1>

      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Setup Clients</h2>
          <p className="max-w-sm text-sm text-gray-700 dark:text-gray-400">
            Manage client workspaces, monitor usage, and track overall reseller performance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative" ref={rangeRef}>
            <button
              type="button"
              onClick={() => setRangeOpen((o) => !o)}
              className="flex items-center gap-2 h-11 px-3 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-200"
            >
              <CalendarDays className="w-4 h-4" />
              {rangeLabel(range.days)}
              <ChevronDown className={`w-4 h-4 ml-3 transition-transform ${rangeOpen ? "rotate-180" : ""}`} />
            </button>
            {rangeOpen && (
              <div className="absolute right-0 z-20 mt-1 w-full min-w-[180px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1">
                {RANGE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setRange(p);
                      setRangeOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      p.id === range.id ? "font-semibold text-indigo-700 dark:text-indigo-400" : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate("/reseller/clients/new")}
            className="h-11 px-3 rounded-md bg-indigo-700 hover:bg-indigo-800 text-sm font-medium text-white transition-colors"
          >
            Add New Client
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <StatCard key={c.title} {...c} />
        ))}
      </div>

      <div ref={tableRef} className="mt-10">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Client Listing Table</h3>
        <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 text-left text-gray-900 dark:text-gray-100">
              <tr>
                {["Client name", "Current Plan", "Added On", "Actions"].map((h) => (
                  <th key={h} className="px-5 py-3.5 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {SAMPLE_CLIENTS.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-16 text-center text-gray-500 dark:text-gray-400">
                    No clients yet — add your first client to get started.
                  </td>
                </tr>
              ) : (
                SAMPLE_CLIENTS.map((c) => (
                  <tr key={c.id} className="text-gray-700 dark:text-gray-300">
                    <td className="px-5 py-3.5 font-medium text-gray-900 dark:text-gray-100">{c.name}</td>
                    <td className="px-5 py-3.5">{c.plan}</td>
                    <td className="px-5 py-3.5">
                      {new Date(c.addedOn).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-5 py-3.5">
                      <button type="button" title="Actions" className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PartnerConsole;
